import {observer} from 'mobx-react';
import {FormattedMessage} from 'react-intl';
import {Panel, HtmlRender, ActionLink} from 'shared/components';
import {Modal} from 'shared/components/modals';
import {ReviewRow} from 'main/components/flow';
import useModal from 'shared/hooks/useModal';
import {t} from 'shared/core';
import moment from 'moment';
import _ from 'lodash';

const DAY_KEYS = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];

const InterviewerList = ({interviewers}) => {
  return (
    <div className='space-y-1'>
      {interviewers.map(user => <div key={user.id}>{user.name}</div>)}
    </div>
  );
};

const AvailabilityWindowList = ({windows}) => (
  <div className='space-y-1'>
    {_.sortBy(windows, 'dayOfWeek').map((window, index) => (
      <div key={index}>
        <FormattedMessage
          id='recruiting.interview_schedules.edit.review.AVAILABILITY_WINDOW'
          values={{
            day: <FormattedMessage id={`recruiting.interview_schedules.edit.${DAY_KEYS[window.dayOfWeek]}`}/>,
            startTime: moment.utc(window.startTime, moment.ISO_8601).format(t('components.dates.TIMESTAMP_FORMAT')),
            endTime: moment.utc(window.endTime, moment.ISO_8601).format(t('components.dates.TIMESTAMP_FORMAT'))
          }}
        />
      </div>
    ))}
  </div>
);

const InvitationEmailPreviewModal = observer(({isOpen, onHide, interviewSchedule}) => {
  return (
    <Modal
      isOpen={isOpen}
      onHide={() => onHide()}
      size='md'
      title='recruiting.interview_schedules.edit.review.Invitation Email Preview'
    >
      <div className='medium mb1'>{interviewSchedule.emailSubject}</div>
      <div className='border px3 py2 bg-haze rounded h4'>
        <div className='py1'>
          <HtmlRender html={interviewSchedule.emailRichText}/>
        </div>
      </div>
    </Modal>
  );
});

const Review = observer(({uiState}) => {
  const emailPreviewModalOpen = useModal(uiState, 'InvitationEmailPreviewModal');
  const {interviewSchedule, match} = uiState;

  const detailsRoute = `/interview-schedules/${match.params.id}/edit/interview_details`;
  const interviewersRoute = `/interview-schedules/${match.params.id}/edit/interviewers`;
  const availabilityRoute = `/interview-schedules/${match.params.id}/edit/availability`;
  const invitationEmailRoute = `/interview-schedules/${match.params.id}/edit/invitation_email`;

  return (
    <div>
      <Panel.Header
        title={t('recruiting.interview_schedules.edit.review.Review your interview schedule')}
        description={t('recruiting.interview_schedules.edit.review.Hover over a section and click edit to make changes.')}
      />
      <ReviewRow
        title='recruiting.interview_schedules.edit.Name'
        value={interviewSchedule.templateName}
        editRoute={detailsRoute}
      />
      <ReviewRow
        title='recruiting.interview_schedules.edit.Event Title'
        value={interviewSchedule.calendarEventName}
        editRoute={detailsRoute}
      />
      <ReviewRow
        title='recruiting.interview_schedules.edit.Description'
        value={interviewSchedule.eventDescription}
        editRoute={detailsRoute}
      />
      <ReviewRow
        title='recruiting.interview_schedules.edit.Duration'
        editRoute={detailsRoute}
      >
        <FormattedMessage
          id='recruiting.interview_schedules.edit.review.DURATION_MINUTES'
          values={{duration: interviewSchedule.duration}}
        />
      </ReviewRow>
      <ReviewRow
        title='recruiting.interview_schedules.edit.Meeting Location'
        editRoute={detailsRoute}
      >
        {interviewSchedule.location &&
          <FormattedMessage id={`recruiting.interview_schedules.edit.meeting_locations.${interviewSchedule.location}`}/>}
      </ReviewRow>
      <ReviewRow
        title='recruiting.interview_schedules.edit.Interviewers'
        editRoute={interviewersRoute}
      >
        <InterviewerList interviewers={interviewSchedule.interviewers}/>
      </ReviewRow>
      <ReviewRow
        title='recruiting.interview_schedules.edit.Meeting Host'
        value={interviewSchedule.eventHost?.name}
        editRoute={interviewersRoute}
      />
      <ReviewRow
        title='recruiting.interview_schedules.edit.Availability'
        editRoute={availabilityRoute}
      >
        <div>
          <FormattedMessage
            id='recruiting.interview_schedules.edit.review.BOOKING_WINDOW'
            values={{
              value: interviewSchedule.relativeEndDateValue,
              unit: <FormattedMessage id={`recruiting.interview_schedules.edit.units.${interviewSchedule.relativeEndDateUnit}`}/>
            }}
          />
        </div>
        {interviewSchedule.minimumNoticeValue && <div className='mt1'>
          <FormattedMessage
            id='recruiting.interview_schedules.edit.review.MINIMUM_NOTICE'
            values={{
              value: interviewSchedule.minimumNoticeValue,
              unit: <FormattedMessage id={`recruiting.interview_schedules.edit.units.${interviewSchedule.minimumNoticeUnit}`}/>
            }}
          />
        </div>}
        {interviewSchedule.eventBuffer && <div className='mt1'>
          <FormattedMessage
            id='recruiting.interview_schedules.edit.review.EVENT_BUFFER'
            values={{minutes: interviewSchedule.eventBuffer}}
          />
        </div>}
      </ReviewRow>
      <ReviewRow
        title='recruiting.interview_schedules.edit.Weekly Availability'
        editRoute={availabilityRoute}
      >
        <AvailabilityWindowList windows={interviewSchedule.availabilityWindows}/>
      </ReviewRow>
      <ReviewRow
        title='recruiting.interview_schedules.edit.Invitation Email'
        lastRow
      >
        <div className='flex'>
          <FormattedMessage id='recruiting.interview_schedules.edit.review.Invitation email to candidates'/>
          <ActionLink className='ml2' onClick={() => uiState.openInvitationEmailPreviewModal()}>
            <FormattedMessage id='recruiting.interview_schedules.edit.review.preview'/>
          </ActionLink>
        </div>
      </ReviewRow>
      <InvitationEmailPreviewModal
        isOpen={emailPreviewModalOpen}
        onHide={() => uiState.closeInvitationEmailPreviewModal()}
        interviewSchedule={interviewSchedule}
        editRoute={invitationEmailRoute}
      />
    </div>
  );
});

export default Review;
